import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'

// Scientific terms and common phrases for autocompletion
const scientificTerms = [
  // Research methodology
  'methodology',
  'experimental design',
  'control group',
  'dependent variable',
  'independent variable',
  'statistical significance',
  'p-value',
  'confidence interval',
  'sample size',
  'randomized controlled trial',
  'systematic review',
  'meta-analysis',
  
  // Statistics
  'mean',
  'median',
  'mode',
  'standard deviation',
  'variance',
  'correlation coefficient',
  'regression analysis',
  'null hypothesis',
  'alternative hypothesis',
  'Type I error',
  'Type II error',
  'power analysis',
  
  // Common scientific phrases
  'according to',
  'as demonstrated by',
  'furthermore',
  'however',
  'nevertheless',
  'in conclusion',
  'the results indicate',
  'the findings suggest',
  'significantly different',
  'no significant difference',
  'statistically significant',
  'correlation between',
  'causal relationship',
  
  // Units and measurements
  'milligrams per liter',
  'parts per million',
  'degrees Celsius',
  'micrometers',
  'nanometers',
  'milliseconds',
  'kilohertz',
  'megahertz',
  
  // Academic writing
  'literature review',
  'peer review',
  'research question',
  'research hypothesis',
  'data collection',
  'data analysis',
  'qualitative research',
  'quantitative research',
  'mixed methods',
  'case study',
  
  // Scientific fields
  'biochemistry',
  'molecular biology',
  'cell biology',
  'genetics',
  'neuroscience',
  'psychology',
  'sociology',
  'anthropology',
  'ecology',
  'environmental science',
  'physics',
  'chemistry',
  'mathematics',
  'computer science',
]

// LaTeX commands for scientific writing
const latexCommands = [
  '\\alpha',
  '\\beta',
  '\\gamma',
  '\\delta',
  '\\epsilon',
  '\\theta',
  '\\lambda',
  '\\mu',
  '\\pi',
  '\\sigma',
  '\\phi',
  '\\omega',
  '\\sum',
  '\\int',
  '\\frac{}{}',
  '\\sqrt{}',
  '\\times',
  '\\div',
  '\\pm',
  '\\leq',
  '\\geq',
  '\\neq',
  '\\approx',
  '\\infty',
  '\\partial',
  '\\nabla',
  '\\begin{equation}',
  '\\end{equation}',
  '\\begin{align}',
  '\\end{align}',
  '\\begin{matrix}',
  '\\end{matrix}',
  '\\textbf{}',
  '\\textit{}',
  '\\cite{}',
  '\\ref{}',
  '\\label{}',
  '\\section{}',
  '\\subsection{}',
  '\\subsubsection{}',
  '\\paragraph{}',
  '\\emph{}',
  '\\footnote{}',
]

// Citation formats
const citationFormats = [
  '(Author et al., Year)',
  '(Author, Year)',
  '[1]',
  '[Author Year]',
  'Author et al. (Year)',
  'Author (Year)',
]

// Academic phrases by context
const academicPhrases = {
  introduction: [
    'The purpose of this study is to',
    'This research aims to',
    'The objective of this investigation is to',
    'Previous studies have shown that',
    'There is growing evidence that',
    'It has been well established that',
    'Despite extensive research, the relationship between',
    'To the best of our knowledge, this is the first study to',
  ],
  
  methods: [
    'Participants were recruited from',
    'The experimental protocol was approved by',
    'Data were collected using',
    'Statistical analysis was performed using',
    'The significance level was set at',
    'Inclusion criteria included',
    'Exclusion criteria included',
    'The study design was a',
  ],
  
  results: [
    'The results showed that',
    'A significant difference was observed between',
    'No significant difference was found',
    'The correlation analysis revealed',
    'Post-hoc analysis indicated',
    'The effect size was',
    'Baseline characteristics are presented in',
    'As shown in Figure',
    'As presented in Table',
  ],
  
  discussion: [
    'These findings suggest that',
    'The results are consistent with',
    'This finding is in agreement with',
    'In contrast to previous studies',
    'A possible explanation for this result is',
    'The clinical implications of these findings',
    'The limitations of this study include',
    'Future research should investigate',
    'In conclusion, our study demonstrates',
  ]
}

export function scientificAutocompletion(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/\w*/)
  if (!word) return null
  
  const input = word.text.toLowerCase()
  if (input.length < 2) return null
  
  // Don't handle backslashes - let LaTeX autocomplete handle those
  if (input.startsWith('\\')) {
    return null
  }
  
  const completions = []
  
  // Scientific terms
  for (const term of scientificTerms) {
    if (term.toLowerCase().includes(input)) {
      completions.push({
        label: term,
        type: 'keyword',
        info: 'Scientific term',
      })
    }
  }
  
  // Citation formats (if text contains opening parenthesis or bracket)
  if (input.includes('(') || input.includes('[')) {
    for (const format of citationFormats) {
      if (format.toLowerCase().includes(input.replace(/[()[\]]/g, ''))) {
        completions.push({
          label: format,
          type: 'text',
          info: 'Citation format',
        })
      }
    }
  }
  
  // Academic phrases by context detection
  const line = context.state.doc.lineAt(context.pos)
  const lineText = line.text.toLowerCase()
  
  // Simple context detection
  let phraseCategory = 'introduction'
  if (lineText.includes('method') || lineText.includes('procedure')) {
    phraseCategory = 'methods'
  } else if (lineText.includes('result') || lineText.includes('finding')) {
    phraseCategory = 'results'
  } else if (lineText.includes('discussion') || lineText.includes('conclusion')) {
    phraseCategory = 'discussion'
  }
  
  const phrases = academicPhrases[phraseCategory as keyof typeof academicPhrases]
  for (const phrase of phrases) {
    if (phrase.toLowerCase().includes(input)) {
      completions.push({
        label: phrase,
        type: 'text',
        info: `Academic phrase (${phraseCategory})`,
      })
    }
  }
  
  if (completions.length === 0) return null
  
  return {
    from: word.from,
    options: completions.slice(0, 10), // Limit to top 10 results
  }
}